import apiClient from './api'

// Type definitions for MSP features
export interface MSPRelationship {
  mspTenantID: string
  companyName: string
  serviceLevel: string
  verifiedAt: string
  guestUserEmail: string
  invitationSentAt?: string
  invitationAcceptedAt?: string
  consentGranted: boolean
  isActive: boolean
  status?: string
}

export interface MSPStatusResponse {
  hasVerificationCode: boolean
  verificationCode: string | null
  codeCreatedAt: string | null
  relationshipStatus: string | null
  mspTenantId: string | null
  mspCompanyName: string | null
}

export interface VerifyDNSResponse {
  verified: boolean
  message: string
  status: string
  domain: string
  emailSendStatus?: string
  emailError?: string | null
}

export interface VerificationCodeResponse {
  verificationCode: string
  instructions: string
  domain?: string
  /** The MSP tenant this code is exclusively authorized for (null = legacy unscoped code). */
  authorizedMspTenantId?: string | null
  emailSendStatus?: string
  emailError?: string | null
}

export interface RevokeMSPAccessResponse {
  success: boolean
  message: string
}

const pickFirst = <T>(...values: (T | null | undefined)[]): T | undefined => {
  for (const value of values) {
    if (value !== null && value !== undefined) {
      return value
    }
  }
  return undefined
}

const decodeResponsePayload = (payload: any): any => {
  if (!payload) {
    return {}
  }

  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload)
    } catch {
      return {}
    }
  }

  if (typeof payload === 'object' && payload.data) {
    return decodeResponsePayload((payload as any).data)
  }

  if (typeof payload === 'object' && payload.body) {
    const body = (payload as any).body
    if (typeof body === 'string') {
      try {
        return JSON.parse(body)
      } catch {
        return payload
      }
    }

    if (typeof body === 'object' && body !== null) {
      return body
    }
  }

  return payload
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const getVerificationCodeFromData = (data: any): string => {
  return pickFirst<string>(
    data?.verificationCode,
    data?.VerificationCode,
    data?.code,
    data?.Code
  ) ?? ''
}

/**
 * MSP customer endpoints are currently authorized via function key + tenant route checks.
 * Avoid triggering interactive MSAL popups for these calls.
 */
const getAuthHeaders = async () => ({})

/**
 * Generate MSP verification code for DNS setup.
 * The customer must identify their MSP partner before the code is created so
 * that only that MSP can complete the verification (prevents unauthorized MSP
 * claims via a public DNS TXT record).
 */
export const generateVerificationCode = async (
  tenantId: string,
  mspIdentity: { mspTenantId?: string; mspEmailDomain?: string }
): Promise<VerificationCodeResponse> => {
  const headers = await getAuthHeaders()
  const response = await apiClient.post(
    `/tenants/${tenantId}/msp/generate-verification-code`,
    mspIdentity,
    { headers }
  )

  const data: any = decodeResponsePayload(response.data)
  let verificationCode = getVerificationCodeFromData(data)

  // If generate endpoint response shape is unexpected, fallback to the status endpoint
  // which reads the newly persisted code from the customer record.
  if (!verificationCode) {
    const retryDelayMs = [250, 500, 1000]
    for (const delayMs of retryDelayMs) {
      const status = await getMSPStatus(tenantId)
      verificationCode = status.verificationCode ?? ''
      if (verificationCode) {
        break
      }
      await sleep(delayMs)
    }
  }

  return {
    verificationCode,
    instructions: pickFirst<string>(
      data.instructions,
      data.Instructions
    ) ?? '',
    domain: pickFirst<string>(
      data.domain,
      data.Domain
    ),
    authorizedMspTenantId: pickFirst<string | null>(
      data.authorizedMspTenantId,
      data.AuthorizedMspTenantId
    ) ?? null,
    emailSendStatus: pickFirst<string>(
      data.emailSendStatus,
      data.EmailSendStatus
    ),
    emailError: pickFirst<string | null>(
      data.emailError,
      data.EmailError
    ) ?? null
  }
}

/**
 * Get all MSP relationships for customer
 */
export const getMSPRelationships = async (tenantId: string): Promise<MSPRelationship[]> => {
  const headers = await getAuthHeaders()
  const response = await apiClient.get(
    `/tenants/${tenantId}/msp/relationships`,
    { headers }
  )
  // Ensure we always return an array
  return Array.isArray(response.data) ? response.data : []
}

/**
 * Revoke MSP access
 */
export const revokeMSPAccess = async (tenantId: string, mspTenantId: string): Promise<RevokeMSPAccessResponse> => {
  const headers = await getAuthHeaders()
  const response = await apiClient.delete(
    `/tenants/${tenantId}/msp/relationships/${mspTenantId}`,
    { headers }
  )
  return response.data
}

/**
 * Get MSP relationship status for customer
 */
export const getMSPStatus = async (tenantId: string): Promise<MSPStatusResponse> => {
  const headers = await getAuthHeaders()
  const response = await apiClient.get(
    `/tenants/${tenantId}/msp/status`,
    { headers }
  )

  const data: any = decodeResponsePayload(response.data)
  return {
    hasVerificationCode: Boolean(pickFirst(
      data.hasVerificationCode,
      data.HasVerificationCode
    )),
    verificationCode: pickFirst<string | null>(
      data.verificationCode,
      data.VerificationCode
    ) ?? null,
    codeCreatedAt: pickFirst<string | null>(
      data.codeCreatedAt,
      data.CodeCreatedAt
    ) ?? null,
    relationshipStatus: pickFirst<string | null>(
      data.relationshipStatus,
      data.RelationshipStatus,
      data.mspRelationshipStatus,
      data.MSP_RelationshipStatus
    ) ?? null,
    mspTenantId: pickFirst<string | null>(
      data.mspTenantId,
      data.MspTenantId,
      data.MSPTenantID
    ) ?? null,
    mspCompanyName: pickFirst<string | null>(
      data.mspCompanyName,
      data.MspCompanyName,
      data.MSPCompanyName
    ) ?? null,
  }
}

/**
 * Customer verifies their own DNS TXT record
 */
export const verifyMyDNS = async (tenantId: string): Promise<VerifyDNSResponse> => {
  const headers = await getAuthHeaders()
  const response = await apiClient.post(
    `/tenants/${tenantId}/msp/verify-my-dns`,
    {},
    { headers }
  )
  const data: any = decodeResponsePayload(response.data)
  return {
    verified: Boolean(pickFirst(data.verified, data.Verified)),
    message: pickFirst<string>(data.message, data.Message) ?? '',
    status: pickFirst<string>(data.status, data.Status) ?? '',
    domain: pickFirst<string>(data.domain, data.Domain) ?? '',
    emailSendStatus: pickFirst<string>(data.emailSendStatus, data.EmailSendStatus),
    emailError: pickFirst<string | null>(data.emailError, data.EmailError) ?? null
  }
}
